import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/ui-bits";
import { FIRM } from "@/data/firm";
import { ATTORNEYS } from "@/data/attorneys";
import LEGAL_UPDATED from "@/data/legal-updated.json";

const LAST_UPDATED = LEGAL_UPDATED.readable;

export default function Legal() {
  return (
    <Layout>
      <SEO
        title="Legal Notices"
        description="Legal notices, attorney advertising disclosures, and terms governing use of the Shelton Law PLLC website."
        path="/legal"
        noindex={false}
      />

      <section className="border-b border-hairline" data-testid="legal-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14">
          <Eyebrow>Legal Notices</Eyebrow>
          <h1 className="display-1 text-ink mt-6 max-w-4xl">Legal notices.</h1>
          <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
            Disclosures and terms governing use of this website.
          </p>
          <p className="mt-6 section-num" data-testid="legal-last-updated">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16 prose-law space-y-14" data-testid="legal-body">

        <Section id="firm" number="01" title="The Firm">
          <p>
            {FIRM.longName} is a professional limited liability company organized under the laws of the State of Tennessee. The firm advises closely-held companies, founders, and international clients on business transactions and the federal regulatory practices that increasingly bear on them.
          </p>
          <p>
            <strong>Responsible attorney for website content:</strong> {FIRM.attorney}, {FIRM.longName}.<br/>
            <strong>Contact:</strong> <a href={FIRM.emailHref}>{FIRM.email}</a>
          </p>
        </Section>

        <Section id="attorney-advertising" number="02" title="Attorney Advertising">
          <p>
            The information on this website may be considered attorney advertising in some jurisdictions. Prior results do not guarantee a similar outcome. This website is not intended to constitute legal advice or a solicitation for the formation of an attorney-client relationship in any jurisdiction where the firm or any individual attorney is not authorized to practice.
          </p>
        </Section>

        <Section id="no-legal-advice" number="03" title="No Legal Advice">
          <p>
            The content on this website is provided for general informational purposes only. It is not a substitute for legal advice from a qualified attorney with knowledge of your particular circumstances. No reader should act, or refrain from acting, on the basis of any information on this website without first seeking legal counsel from an attorney admitted to practice in the reader&apos;s jurisdiction.
          </p>
        </Section>

        <Section id="no-relationship" number="04" title="No Attorney-Client Relationship">
          <p>
            The transmission of information to or from {FIRM.longName} through this website, by email, or by any other means does not establish an attorney-client relationship between the firm (or any of its attorneys) and the sender. An attorney-client relationship is formed only after the firm has (i) confirmed the absence of conflicts of interest, (ii) agreed in writing to undertake the representation, and (iii) received an executed engagement letter.
          </p>
          <p>
            Please do not send confidential or time-sensitive information to the firm through this website or by email until an attorney-client relationship has been established in writing. Unsolicited communications may not be treated as confidential and may not receive a response.
          </p>
        </Section>

        <Section id="admissions" number="05" title="Jurisdictions of Admission">
          <p>
            The firm&apos;s attorneys are admitted to practice as follows:
          </p>
          <ul>
            {ATTORNEYS.map((a) => (
              <li key={a.slug}>
                <strong>{a.name}</strong> — {a.admissions.join("; ")}.
              </li>
            ))}
          </ul>
          <p>
            Representation in jurisdictions other than those listed above is undertaken only where permitted by local rules, including through pro hac vice admission or association with local counsel.
          </p>
        </Section>

        <Section id="use" number="06" title="Use of This Website">
          <p>
            You may view and download materials from this website for your own non-commercial reference use only, provided that you retain all copyright and other proprietary notices. Except as expressly permitted, no material from this website may be copied, reproduced, republished, uploaded, posted, transmitted, or distributed in any way without the firm&apos;s prior written permission.
          </p>
          <p>
            The firm makes no representation that materials on this website are appropriate or available for use in jurisdictions outside the United States. Access from jurisdictions where such content is unlawful is prohibited.
          </p>
        </Section>

        <Section id="third-party-links" number="07" title="Third-Party Links">
          <p>
            This website may contain links to third-party websites. Such links are provided as a convenience only; the firm does not endorse and is not responsible for the content, privacy practices, or accuracy of any third-party website.
          </p>
        </Section>

        <Section id="copyright" number="08" title="Copyright">
          <p>
            © {new Date().getFullYear()} {FIRM.longName}. All rights reserved.
          </p>
        </Section>

        <Section id="changes" number="09" title="Changes to These Notices">
          <p>
            The firm may update these notices from time to time. The revised version will be posted on this page with a new &ldquo;Last updated&rdquo; date. Continued use of the website after any change constitutes acceptance of the revised terms.
          </p>
        </Section>

        <Section id="contact" number="10" title="Questions">
          <p>
            Questions about these notices may be directed to <a href={FIRM.emailHref}>{FIRM.email}</a>.
          </p>
        </Section>

      </section>
    </Layout>
  );
}

function Section({ id, number, title, children }) {
  return (
    <div id={id} data-testid={`legal-section-${id}`} className="scroll-mt-24">
      <div className="flex items-baseline gap-4 mb-5">
        <span className="section-num">{number}</span>
        <h2 className="font-serif text-2xl sm:text-3xl text-ink leading-tight tracking-tight m-0">{title}</h2>
      </div>
      <div className="pl-0">{children}</div>
    </div>
  );
}
